import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Select,
  Button,
  Tooltip,
  Table,
  Input,
  Tag,
  Modal,
  Form,
  DatePicker,
  Divider,
  Card,
  notification,
  Checkbox,
} from "antd";
import {
  DeleteFilled,
  EditOutlined,
  FileExcelOutlined,
  PlusOutlined,
  SyncOutlined,
  DollarOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import "../Pages/CSS/Traitement.css";
import { BpiService } from "../Services/BpiService";
import { ExerciceDocService } from "../Services/ExerciceDocService";
import ExportOrdrepaie from "./ExportOrdrepaie";
import { JournauxComptableService } from "../Services/JournauxComptableService";
import moment from "moment";
import "moment/locale/fr";
import dayjs from 'dayjs'
import * as XLSX from "xlsx";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
const { confirm } = Modal;
const { Option } = Select;

const OrdrePaie = () => {
  const bpiService = useMemo(() => new BpiService(), []);
  const journalService = useMemo(() => new JournauxComptableService(), []);
  const exerciceDocService = useMemo(() => new ExerciceDocService(), []);

  const getSelectedYear = () => new Date().getFullYear().toString();

  const [form] = Form.useForm();
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getSelectedYear());
  const [refreshTable, setRefreshTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [journalData, setJournalData] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState("C");
  const [years, setYears] = useState([]);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editData, setEditData] = useState(null); // État pour stocker les données de la ligne sélectionnée pour l'édition

  // Définir fetchData AVANT useEffect
  const fetchData = useCallback(async (year) => {
    setLoading(true);
    try {
      const orderletter = await bpiService.getAllODP(year);
      setTableData(orderletter);
      setFilteredData(orderletter);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notification.error({
        message: "Erreur lors de la récupération des données",
        description: error.message,
      });
    }
  }, [bpiService]);

  useEffect(() => {
    moment.locale("fr");
    const fetchInitialData = async () => {
      try {
        const yearsData = await exerciceDocService.getAllExLettre();
        setYears([...yearsData].sort((a, b) => b - a));
        await fetchData(selectedYear);
      } catch (error) {
        notification.error({
          message: "Erreur lors de la récupération des données",
          description: error.message,
        });
      }
    };

    fetchInitialData();
    // Supprimez modalData?.freelValue de la liste des dépendances
  }, [exerciceDocService, fetchData, selectedYear]);

  useEffect(() => {
    if (filteredData.length > 0) {
      setSelectedColumns(Object.keys(filteredData[0]));
    }
  }, [filteredData]);

  /* useEffect(() => {
    fetchData(selectedYear);
  }, [selectedYear, refreshTable]); */

  const handleRefresh = () => {
    setRefreshTable(!refreshTable);
  };

  const handleDeleteLine = useCallback((numligne) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette prestation ?",
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          await bpiService.deleteOrdrePaie(numligne);
          setFilteredData(
            filteredData.filter((item) => item.numligne !== numligne)
          );
        } catch (error) {
          notification.error({
            message: "Erreur lors de la suppression",
            description: error.message,
          });
        }
      },
    });
  }, [bpiService, filteredData]);

  const handleYearChange = async (value) => {
    setSelectedYear(value);
    await fetchData(value); // Recharge les données pour l'année sélectionnée
  };

  const handleSearch = (value) => {
    const filtered = tableData.filter((item) =>
      Object.values(item).some((val) =>
        (val !== null && val !== undefined ? val.toString() : "")
          .toLowerCase()
          .includes(value.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const fetchSaisieJournal = useCallback(async (type) => {
    try {
      const journalsData = await journalService.getAllJournauxComptable();
      setJournalData(
        journalsData.filter((journal) => journal.type === type)
      );
    } catch (error) {
      notification.error({
        message: "Erreur lors de la récupération des journaux comptables",
        description: error.message,
      });
    }
  }, [journalService]);

  const handleOpenModal = useCallback((data) => {
    const paymentType = data ? data.mode_paiement : "C";
    fetchSaisieJournal(paymentType === "T" ? "TRANSFERT" : "BANQUE");

    if (data) {
      form.setFieldsValue({
        ...data,
        date_cheque: data.date_cheque ? dayjs(moment(data.date_cheque)) : null,
      });
      setEditData(data);
      setSelectedPaymentType(paymentType);
    } else {
      form.resetFields();
      form.setFieldsValue({ date_cheque: dayjs(moment()) });
      setEditData(null);
      setSelectedPaymentType("C");
    }
    setModalVisible(true);
  }, [form, fetchSaisieJournal]);

  const handleCloseModal = () => {
    setModalVisible(false);
  };


  const handleSave = async (formData) => {
    try {
      if (editData) {
        formData.numligne = editData.numligne; // Assurez-vous d'inclure l'identifiant unique pour la mise à jour
        await bpiService.UpdateOrdrePaie(formData);
        notification.success({
          message: "Ordre de paiement mis à jour avec succès",
        });
      } else {
        await bpiService.AddOrdrePaie(formData);
        notification.success({
          message: "Ordre de paiement ajouté avec succès",
        });
      }
      await fetchData(selectedYear);
      form.resetFields();
      setModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Erreur lors de l'enregistrement de l'ordre de paiement",
        description: error.message,
      });
    }
  };

  const handlePaymentTypeChange = (value) => {
    setSelectedPaymentType(value);
    fetchSaisieJournal(value === "T" ? "TRANSFERT" : "BANQUE");
  };

  const handleExportModalOk = () => {
    const selectedRows = filteredData.filter((row) =>
      selectedRowKeys.includes(row.numpiece)
    );

    if (selectedRows.length === 0) {
      notification.warning({
        message: "Aucune ligne sélectionnée pour l'exportation",
      });
      return;
    }

    const columnsToExport =
      selectedColumns.length > 0
        ? selectedColumns
        : Object.keys(filteredData[0]);
    const dataToExport = selectedRows.map((row) => {
      const rowData = {};
      columnsToExport.forEach((column) => {
        rowData[column] = row[column];
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ordres de Paiement");

    XLSX.writeFile(workbook, "ordres_de_paiement.xlsx");
    setIsExportModalVisible(false);
  };

  const handleExportModalCancel = () => {
    setIsExportModalVisible(false);
  };

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const allRowKeys = filteredData.map((record) => record.numpiece);

  const handleSelectAllChange = (e) => {
    setSelectedRowKeys(e.target.checked ? allRowKeys : []);
  };

  const rowSelection = {
    selectedRowKeys,
    columnTitle: (
      <Checkbox
        checked={allRowKeys.length > 0 && selectedRowKeys.length === allRowKeys.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < allRowKeys.length}
        onChange={handleSelectAllChange}
      />
    ),
    onChange: onSelectChange,
  };
  function formatNumber(number) {
    return new Intl.NumberFormat("fr-FR").format(number);
  }

  const columns = useMemo(
    () => [
      {
        title: "N° Enreg.",
        dataIndex: "numpiece",
        key: "numpiece",
        align: "center",
        sorter: (a, b) => (a.numpiece || "").localeCompare(b.numpiece || ""),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Date Saisie",
        dataIndex: "date_saisie",
        key: "date_saisie",
        align: "center",
        render: (text) => (text ? moment(text).format("DD/MM/YYYY") : "N/A"),
        sorter: (a, b) => {
          const dateA = a.date_saisie ? new Date(a.date_saisie) : 0;
          const dateB = b.date_saisie ? new Date(b.date_saisie) : 0;
          return dateA - dateB;
        },
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "N° piece",
        dataIndex: "num_cheque",
        key: "numcheque",
        align: "center",
        sorter: (a, b) => (a.num_cheque || "").localeCompare(b.num_cheque || ""),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Date pièce",
        dataIndex: "date_cheque",
        key: "date_cheque",
        align: "center",
        render: (text) => (text ? moment(text).format("DD/MM/YYYY") : "N/A"),
        sorter: (a, b) => {
          const dateA = a.date_cheque ? new Date(a.date_cheque) : 0;
          const dateB = b.date_cheque ? new Date(b.date_cheque) : 0;
          return dateA - dateB;
        },
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Montant",
        dataIndex: "mont_cheque",
        key: "mont_cheque",
        align: "right",
        fontWeight: "bold",
        render: (amount) => (
          <span style={{ paddingRight: "5px" }}>
            <strong>{formatNumber(amount || 0)}</strong>
          </span>
        ),
        sorter: (a, b) => (a.mont_cheque || 0) - (b.mont_cheque || 0),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Ordre de",
        dataIndex: "libelle",
        key: "libelle",
        sorter: (a, b) => (a.libelle || "").localeCompare(b.libelle || ""),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Motif",
        dataIndex: "motif",
        key: "motif",
        sorter: (a, b) => (a.motif || "").localeCompare(b.motif || ""),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Mode paiement",
        dataIndex: "mode_paiement",
        align: "center",
        render: (mode_paiement) => {
          let color = "";
          let text = "";

          if (mode_paiement === "C") {
            color = "#67f02e";
            text = "Cheque";
          } else if (mode_paiement === "V") {
            color = "#d4c222";
            text = "Virement";
          } else if (mode_paiement === "E") {
            color = "#3232a8";
            text = "Espéce";
          }

          return (
            <Tag color={color} key={mode_paiement}>
              {text}
            </Tag>
          );
        },
        sorter: (a, b) => (a.mode_paiement || "").localeCompare(b.mode_paiement || ""),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: (
          <div
            style={{
              backgroundColor: "#010f1c",
              color: "white",
              padding: "1px",
            }}
          ></div>
        ),
        dataIndex: "actions",
        key: "actions",
        align: "center",
        fixed: "right",
        render: (text, record) => (
          <span>
            <Tooltip title="Imprimer">
              <ExportOrdrepaie numpiece={record.numpiece} mode_paiement={record.mode_paiement} />
            </Tooltip>
            <Tooltip title="Modifier">
              <EditOutlined
                style={{ cursor: "pointer", color: "orange", marginRight: 8 }}
                onClick={() => handleOpenModal(record)}
              />
            </Tooltip>
            <Tooltip title="Supprimer">
              <DeleteFilled
                style={{ cursor: "pointer", color: "red" }}
                onClick={() => handleDeleteLine(record.numligne)}
              />
            </Tooltip>
          </span>
        ),
      },
    ],
    [handleDeleteLine, handleOpenModal]
  );

  const selectionMenu = (
    <Menu>
      <Menu.Item
        key="selectAll"
        onClick={() => {
          const allKeys = filteredData.map(row => row.numpiece);
          setSelectedRowKeys(allKeys);
        }}
      >
        Tout sélectionner
      </Menu.Item>
      <Menu.Item
        key="deselectAll"
        onClick={() => {
          setSelectedRowKeys([]);
        }}
      >
        Tout désélectionner
      </Menu.Item>
    </Menu>
  );

  return (
    <div>
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "40px" }}
        >
          <Col>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 20px",
                background: "linear-gradient(135deg, #1890ff 0%, #0097e6 100%)",
                borderRadius: 10,
                boxShadow: "0 4px 15px rgba(24, 144, 255, 0.3)",
                marginBottom: 16,
              }}
            >
              <DollarOutlined style={{ fontSize: 22, color: "#fff" }} />
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#fff",
                  letterSpacing: 0.5,
                  margin: 0,
                }}
              >
                Ordres de Paiement
              </span>
            </div>
            <Select
              className="select-colored-accent"
              style={{
                width: 120,
                marginRight: 10,
                "--select-accent": "#1890ff",
                "--select-accent-bg": "#e6f4ff",
                "--select-accent-shadow": "rgba(24, 144, 255, 0.25)",
              }}
              onChange={handleYearChange}
              value={selectedYear}
            >
              {years.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>

            <Input
              className="search-input-highlight"
              placeholder="Rechercher..."
              allowClear
              prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
              style={{ width: 250, marginRight: 10 }}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {/* <Dropdown overlay={selectionMenu} trigger={["click"]}>
              <Button style={{ marginRight: 10, backgroundColor: "#87CEEB" }}>
                Sélection <DownOutlined />
              </Button>
            </Dropdown> */}
          </Col>
          <Col>
            <Button
              className="buttonRecep"
              style={{ marginRight: 10 }}
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal(null)}
            >
              Nouveau
            </Button>
            <Button
              className="buttonExport"
              style={{ marginRight: 10 }}
              icon={<FileExcelOutlined />}
              onClick={() => {
                if (selectedRowKeys.length === 0) {
                  Modal.info({
                    title: "Aucune ligne sélectionnée pour l'exportation",
                    content: "",
                    okText: "OK",
                  });
                  return;
                }
                setIsExportModalVisible(true);
              }}
            >
              Export
            </Button>
            <Button
              className="buttonRefresh"
              icon={<SyncOutlined />}
              onClick={handleRefresh}
            />
          </Col>
        </Row>
        <Table
          scroll={{ x: true }}
          style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
          className="custom-header2-table"
          bordered
          columns={columns}
          showSorterTooltip={false}
          loading={loading}
          dataSource={filteredData}
          pagination={{
            pageSizeOptions: ["5", "10", "20", "50", "100"], // Options disponibles pour la pagination
            showSizeChanger: true, // Permet à l'utilisateur de changer le nombre de lignes par page
            pageSize: pageSize, // Valeur actuelle du nombre de lignes par page
            onShowSizeChange: (current, size) => setPageSize(size), // Mise à jour de pageSize lors du changement
          }}
          rowSelection={rowSelection}
          rowKey="numpiece"
        />
        <Modal
          title="Export Ordres de Paiement"
          open={isExportModalVisible}
          onOk={handleExportModalOk}
          onCancel={handleExportModalCancel}
          maskClosable={false}
        >
          <Checkbox.Group
            style={{ display: "flex", flexDirection: "column" }}
            options={Object.keys(filteredData[0] || {}).map((col) => ({
              label: col,
              value: col,
            }))}
            value={selectedColumns}
            onChange={(checkedValues) => setSelectedColumns(checkedValues)}
          />
        </Modal>
        <Modal
          title={
            editData
              ? "Modification Ordre paiement "
              : "Ajout Ordre de Paiement"
          }
          open={isModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          maskClosable={false}
          style={{ top: 20 }}
          width={600}
        >
          <Form layout="vertical" form={form} onFinish={handleSave}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Mode de paiement"
                  name="mode_paiement"
                  rules={[
                    { required: true, message: "Champ requis", noStyle: true },
                  ]}
                >
                  <Select
                    value={selectedPaymentType}
                    onChange={handlePaymentTypeChange}
                  >
                    <Option value="C">Chèque</Option>
                    <Option value="V">Virement</Option>
                    <Option value="E">Espece</Option>
                    <Option value="T">Transfert</Option>
                  </Select>
                </Form.Item>
              </Col>
              {selectedPaymentType !== "E" && (
                <Col span={12}>
                  <Form.Item
                    label={selectedPaymentType === "T" ? "Transfert" : "Banque"}
                    name="code_journal"
                    rules={[{ required: true, message: "Champ requis" }]}

                  >
                    <Select
                      style={{ width: "100%" }}
                      defaultValue={
                        "--Selectionner--"
                      }
                    >
                      {journalData.map((journal) => (
                        <Option key={journal.codej} value={journal.codej}>
                          {`${journal.codej}-${journal.libelle} `}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              {selectedPaymentType === "E" && (
                <Col span={12}>
                  <Form.Item
                    label="Montant espèce"
                    name="montant_espece"
                    rules={[{ required: true, message: "Champ requis" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {selectedPaymentType === "V" && (
              <>
                <Divider orientation="left">
                  Infos Banques Bénéficiaire
                </Divider>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="Code Banque"
                      name="code_bque"
                      rules={[{ required: true, message: "Champ requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Nom Banque"
                      name="nom_bque"
                      rules={[{ required: true, message: "Champ requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Code Guichet"
                      name="code_guichet"
                      rules={[{ required: true, message: "Champ requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Compte Banque"
                      name="compte_bque"
                      rules={[{ required: true, message: "Champ requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="RIB"
                      name="rib"
                      rules={[{ required: true, message: "Champ requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider />
              </>
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date paiement"
                  name="date_cheque"
                  rules={[{ required: true, message: "Champ requis" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Montant"
                  name="mont_cheque"
                  rules={[{ required: true, message: "Champ requis" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="N° Pièce (chèque, virement...)"
              name="num_cheque"
              rules={[{ required: true, message: "Champ requis" }]}
            >
              <Input />
            </Form.Item>

            {/* <Form.Item

              name="solde"

            >
              <strong>Solde Banque ce jour</strong> :
            </Form.Item> */}
            <Form.Item
              label="Motif"
              name="motif"
              rules={[{ required: true, message: "Champ requis" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="À l'ordre de"
              name="libelle"
              rules={[{ required: true, message: "Champ requis" }]}
            >
              <Input />
            </Form.Item>
            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <Form.Item>
                <Button onClick={handleCloseModal} type="dashed" danger>
                  Annuler
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  style={{ backgroundColor: "#010f1c", marginLeft: 8 }}
                >
                  Valider
                </Button>
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default OrdrePaie;
